import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";

@Component({
    selector: "[window-type-2]",
    templateUrl: "./window-type-2.component.html",
    styleUrls: ["./window-type-2.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WindowType2Component {
  height = input<number>();
  windowMargin = input<number>();

  leftWindows = computed(() => this.generateWindows("left"));
  rightWindows = computed(() => this.generateWindows("right"));

  /**
   * Returns configuration settings based on the building type.
   * @returns Config object containing window placement parameters.
   */

  /**
   * Generates windows for the left or right side of the building.
   * @param side - The side of the building ('left' or 'right').
   * @returns Array of window objects.
   */
  private generateWindows(side: "left" | "right") {
    const leftOffset = 200;
    const rowSize = 4;
    const margin = 10;

    if (side === "left") {
      return this.generateLeftWindows(leftOffset);
    }

    return this.generateRightWindows(rowSize, margin);
  }

  /**
   * Generates left-side windows based on building height.
   * @param offsetHeight - The starting height for window placement.
   * @returns Array of window objects.
   */
  private generateLeftWindows(offsetHeight: number) {
    const heightIncrement = 50;
    const count = Math.floor((this.height() - offsetHeight) / heightIncrement) + 1;

    return Array.from({ length: count }, () => ({
      isLit: Math.random() > 0.5,
    }));
  }

  /**
   * Generates right-side windows based on given parameters.
   * @param rowSize - Number of windows per row.
   * @param margin - Vertical margin between rows.
   * @returns Array of window objects.
   */
  private generateRightWindows(rowSize: number, margin: number) {
    const heightIncrement = 33;
    const numberOfRows = Math.floor((this.height() - 200) / heightIncrement) + 1;
    const totalWindows = numberOfRows * rowSize;

    return Array.from({ length: totalWindows }, (_, i) => {
      const rowIndex = Math.floor(i / rowSize);
      const columnIndex = i % rowSize;
      const x = 130 + 26 * columnIndex;
      const y = 153 + margin - 15 * columnIndex + rowIndex * 35;

      return {
        maskId: `mask_${i + 1}_${'window-type-2'}`,
        maskX: x,
        maskY: y,
        maskWidth: 15,
        maskHeight: 40,
        maskPath: this.generateMaskPath(x, y),
        windowRightLitPath: this.generateWindowRightLitPath(x, y),
        windowShadePath: this.generateWindowShadePath(x, y),
        isLit: Math.random() > 0.5,
      };
    });
  }

  /**
   * Generates an SVG mask path for the window.
   * @param x - The x coordinate.
   * @param y - The y coordinate.
   * @returns SVG path string.
   */
  private generateMaskPath(x: number, y: number): string {
    const width = 14.5;
    return `M${x} ${y + 9.432}V${y + 39.732}L${x + width} ${y + 31.095}V${y + 0.76}L${x} ${y + 9.432}Z`;
  }

  /**
   * Generates an SVG path for the right-lit effect.
   * @param x - The x coordinate.
   * @param y - The y coordinate.
   * @returns SVG path string.
   */
  private generateWindowRightLitPath(x: number, y: number): string {
    const x1 = x + 11.5;
    const y1 = y - 5;
    return `M${x1} ${y1} L${x1 - 25} ${y1 + 11} L${x1 - 10} ${y1 + 49} L${x1 + 15} ${y1 + 39} L${x1} ${y1} Z`;
  }

  /**
   * Generates an SVG path for the window shade.
   * @param x - The x coordinate.
   * @param y - The y coordinate.
   * @returns SVG path string.
   */
  private generateWindowShadePath(x: number, y: number): string {
    const width = 14.5;
    return `M${x} ${y + 35.482}L${x + 11.3} ${y + 28.743}V${y + 2.66}L${x + width} ${y + 0.76}V${y + 31.095}L${x} ${y + 39.732}V${y + 35.482}Z`;
  }
}
